import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, Building2, ArrowUpDown, Trash2 } from 'lucide-react';
import api from '../api';
import { Company, NafCodeGroup, PaginatedResult } from '../types';
import ScoreBadge from '../components/ScoreBadge';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('fr-FR');
};

const CompanyListPage: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [search, setSearch] = useState('');
  const [nafGroupId, setNafGroupId] = useState('');
  const [postalPrefix, setPostalPrefix] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [multiSiteFilter, setMultiSiteFilter] = useState('');
  const [scoreMin, setScoreMin] = useState('');
  const [scoreMax, setScoreMax] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');

  // NAF groups for filter
  const [nafGroups, setNafGroups] = useState<NafCodeGroup[]>([]);

  // Add company modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCompany, setNewCompany] = useState({ siren: '', name: '' });
  const [addError, setAddError] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  useEffect(() => {
    api.get('/naf-codes/groups').then((res) => {
      setNafGroups(res.data.data || res.data || []);
    }).catch(() => {});
  }, []);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: any = { page, limit, sortBy, sortOrder };
      if (search) params.search = search;
      if (nafGroupId) params.nafGroupId = nafGroupId;
      if (postalPrefix) params.postalCodePrefix = postalPrefix;
      if (activeFilter !== '') params.isActive = activeFilter;
      if (multiSiteFilter !== '') params.isMultiSite = multiSiteFilter;
      if (scoreMin) params.scoreMin = scoreMin;
      if (scoreMax) params.scoreMax = scoreMax;

      const res = await api.get<PaginatedResult<Company>>('/companies', { params });
      setCompanies(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      setError('Erreur lors du chargement des entreprises.');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, nafGroupId, postalPrefix, activeFilter, multiSiteFilter, scoreMin, scoreMax, sortBy, sortOrder]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
    setPage(1);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchCompanies();
  };

  const handleAddCompany = async () => {
    setAddError('');
    if (!newCompany.siren.trim() || !newCompany.name.trim()) {
      setAddError('Le SIREN et le nom sont obligatoires.');
      return;
    }
    setAddLoading(true);
    try {
      const res = await api.post('/companies', newCompany);
      setShowAddModal(false);
      setNewCompany({ siren: '', name: '' });
      navigate(`/companies/${res.data.id}`);
    } catch (err: any) {
      setAddError(err.response?.data?.message || 'Erreur lors de la création.');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeleteCompany = async (e: React.MouseEvent, companyId: string, companyName: string) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer l'entreprise "${companyName}" ? Cette action est irréversible.`)) return;
    try {
      await api.delete(`/companies/${companyId}`);
      fetchCompanies();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erreur lors de la suppression.');
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div className="page-header">
        <h1>Entreprises</h1>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <Plus size={16} />
            Ajouter
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="toolbar">
            <div className="toolbar-left">
              <form onSubmit={handleSearch} className="search-bar">
                <Search />
                <input
                  type="text"
                  placeholder="Rechercher par nom, SIREN..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </form>
            </div>
          </div>

          <div className="filter-bar">
            <select
              className="form-select"
              value={nafGroupId}
              onChange={(e) => { setNafGroupId(e.target.value); setPage(1); }}
            >
              <option value="">Tous les codes NAF</option>
              {nafGroups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>

            <input
              type="text"
              className="form-input"
              placeholder="Code postal..."
              value={postalPrefix}
              onChange={(e) => { setPostalPrefix(e.target.value); setPage(1); }}
              style={{ width: '140px' }}
            />

            <select
              className="form-select"
              value={activeFilter}
              onChange={(e) => { setActiveFilter(e.target.value); setPage(1); }}
            >
              <option value="">Tous statuts</option>
              <option value="true">Actives</option>
              <option value="false">Inactives</option>
            </select>

            <select
              className="form-select"
              value={multiSiteFilter}
              onChange={(e) => { setMultiSiteFilter(e.target.value); setPage(1); }}
            >
              <option value="">Mono/Multi-site</option>
              <option value="true">Multi-site</option>
              <option value="false">Mono-site</option>
            </select>

            <input
              type="number"
              className="form-input"
              placeholder="Score min"
              value={scoreMin}
              onChange={(e) => { setScoreMin(e.target.value); setPage(1); }}
              style={{ width: '110px' }}
              min={0}
              max={100}
            />
            <input
              type="number"
              className="form-input"
              placeholder="Score max"
              value={scoreMax}
              onChange={(e) => { setScoreMax(e.target.value); setPage(1); }}
              style={{ width: '110px' }}
              min={0}
              max={100}
            />
          </div>

          {loading ? (
            <div className="spinner-container">
              <div className="spinner" />
            </div>
          ) : error ? (
            <div className="empty-state">
              <p className="empty-state-title">{error}</p>
            </div>
          ) : companies.length === 0 ? (
            <div className="empty-state">
              <Building2 size={48} />
              <p className="empty-state-title">Aucune entreprise trouvée</p>
              <p className="empty-state-description">Modifiez vos filtres ou ajoutez une entreprise.</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table table-clickable">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort('name')}>
                      <span className="inline-flex">
                        Nom
                        {sortBy === 'name' && <ArrowUpDown size={14} />}
                      </span>
                    </th>
                    <th>SIREN</th>
                    <th>NAF</th>
                    <th className="sortable" onClick={() => handleSort('addressCity')}>
                      <span className="inline-flex">
                        Ville
                        {sortBy === 'addressCity' && <ArrowUpDown size={14} />}
                      </span>
                    </th>
                    <th className="sortable" onClick={() => handleSort('score')}>
                      <span className="inline-flex">
                        Score
                        {sortBy === 'score' && <ArrowUpDown size={14} />}
                      </span>
                    </th>
                    <th>Statut</th>
                    <th className="sortable" onClick={() => handleSort('createdAt')}>
                      <span className="inline-flex">
                        Créé le
                        {sortBy === 'createdAt' && <ArrowUpDown size={14} />}
                      </span>
                    </th>
                    <th style={{ width: '60px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company) => (
                    <tr key={company.id} onClick={() => navigate(`/companies/${company.id}`)}>
                      <td className="font-semibold">{company.name}</td>
                      <td>{company.siren}</td>
                      <td>
                        <span title={company.nafLabel || ''}>
                          {company.nafCode || '-'}
                        </span>
                      </td>
                      <td>{company.addressCity || '-'}</td>
                      <td>
                        <ScoreBadge score={company.score} size="sm" />
                      </td>
                      <td>
                        <span className={`badge ${company.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {company.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{formatDate(company.createdAt)}</td>
                      <td>
                        <button
                          className="btn-icon"
                          onClick={(e) => handleDeleteCompany(e, company.id, company.name)}
                          title="Supprimer"
                        >
                          <Trash2 size={16} color="#EF4444" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            total={total}
          />
        </div>
      </div>

      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Ajouter une entreprise"
        footer={
          <>
            <button className="btn" onClick={() => setShowAddModal(false)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleAddCompany} disabled={addLoading}>
              {addLoading ? <span className="spinner spinner-sm" /> : 'Créer'}
            </button>
          </>
        }
      >
        {addError && <div className="login-error">{addError}</div>}
        <div className="form-group">
          <label className="form-label">SIREN</label>
          <input
            type="text"
            className="form-input"
            placeholder="123456789"
            value={newCompany.siren}
            onChange={(e) => setNewCompany({ ...newCompany, siren: e.target.value })}
            maxLength={9}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Nom de l'entreprise</label>
          <input
            type="text"
            className="form-input"
            placeholder="Nom de l'entreprise"
            value={newCompany.name}
            onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
          />
        </div>
      </Modal>
    </div>
  );
};

export default CompanyListPage;
